import React from 'react';

const HOUSE_MEANINGS = {
  1: 'Identity and first approach',
  2: 'Resources and self-worth',
  3: 'Learning and communication',
  4: 'Roots and foundations',
  5: 'Creativity and expression',
  6: 'Routines and craft',
  7: 'Relationships and mirrors',
  8: 'Depth and shared bonds',
  9: 'Belief and perspective',
  10: 'Direction and contribution',
  11: 'Community and future',
  12: 'Retreat and integration',
};

function ordinalHouse(value) {
  const house = Number(value);
  if (house === 1) return '1st';
  if (house === 2) return '2nd';
  if (house === 3) return '3rd';
  return `${house}th`;
}

function houseTheme(house) {
  const map = {
    1: 'identity, body, and first response',
    2: 'resources, value, and security',
    3: 'language, learning, and local patterning',
    4: 'home, roots, and emotional base',
    5: 'creativity, pleasure, and self-expression',
    6: 'work rhythms, service, and health routines',
    7: 'partnerships, contracts, and direct mirrors',
    8: 'shared trust, intimacy, and transformation',
    9: 'belief, study, and worldview',
    10: 'direction, public role, and vocation',
    11: 'community, friendships, and future aims',
    12: 'retreat, integration, and inner processing',
  };
  return map[Number(house)] || 'the key life themes for this sector';
}

function signTone(sign) {
  const map = {
    Aries: 'direct, initiating expression',
    Taurus: 'steadiness, embodiment, material comfort, or resistance to disruption',
    Gemini: 'curious, adaptable expression',
    Cancer: 'feeling, protection, and emotional memory',
    Leo: 'visible, heart-led expression',
    Virgo: 'precise, refining expression',
    Libra: 'relational, harmonizing expression',
    Scorpio: 'intense, depth-seeking expression',
    Sagittarius: 'expansive, meaning-seeking expression',
    Capricorn: 'structured, pragmatic expression',
    Aquarius: 'independent, perspective-shifting expression',
    Pisces: 'porous, symbolic expression',
  };
  return map[sign] || `${sign} tone`;
}

function HouseInterpretationPanel({ house, chart }) {
  if (!house || !chart) return null;
  const placements = chart.planets || [];
  const planetsInHouse = placements.filter((p) => Number(p.house) === Number(house.house));
  const h = ordinalHouse(house.house);
  const theme = HOUSE_MEANINGS[house.house] || 'Life arena';

  return (
    <div className="chart-accordion-panel" role="status" aria-live="polite">
      <p className="chart-accordion-panel-label">Selected house</p>
      <h4 className="chart-accordion-panel-title">{h} House — {theme}</h4>
      <p className="chart-accordion-panel-body">
        The {h} house describes {houseTheme(house.house)}.
        With {house.sign || 'this sign'} on the cusp, this area may move through {signTone(house.sign)}.
      </p>
      {planetsInHouse.length > 0 && (
        <>
          <h5 className="chart-accordion-panel-sub">Active here now</h5>
          <p className="chart-accordion-panel-body">
            {planetsInHouse.map((p) => `${p.name || p.nameFi || p.key} in ${p.sign}`).join(', ')}.
          </p>
        </>
      )}
      <p className="chart-accordion-panel-body">
        You may notice this house most clearly when life asks for withdrawal, restoration, privacy, or quiet meaning-making.
      </p>
    </div>
  );
}

const HousesTable = ({ houses, selectedHouse, onSelectHouse, chart }) => {
  const handleRowClick = (houseNumber) => {
    const num = Number(houseNumber);
    onSelectHouse(Number(selectedHouse) === num ? null : houseNumber);
  };

  return (
    <div className="chart-table-wrap card">
      <h3>House cusps</h3>
      <p className="chart-interaction-hint">Select a row to expand the interpretation directly below it.</p>
      <table className="chart-table">
        <thead>
          <tr>
            <th>House</th>
            <th>Sign</th>
            <th>Degree</th>
          </tr>
        </thead>
        <tbody>
          {houses.map((house) => (
            <React.Fragment key={`house-${house.house}`}>
              <tr
                className={Number(selectedHouse) === Number(house.house) ? 'chart-row-selected' : ''}
                onClick={() => handleRowClick(house.house)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleRowClick(house.house);
                  }
                }}
              >
                <td>{house.house}</td>
                <td>{house.sign}</td>
                <td>{house.degreeText}</td>
              </tr>
              {Number(selectedHouse) === Number(house.house) && chart && (
                <tr className="chart-accordion-row">
                  <td colSpan={3} className="chart-accordion-cell">
                    <HouseInterpretationPanel
                      house={chart.houses?.find((h) => Number(h.house) === Number(house.house)) || house}
                      chart={chart}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default HousesTable;
